import { StorageAdapter } from '../StorageAdapter'

/**
 * Browser `sessionStorage` wrapper. Per-tab, cleared on tab close.
 *
 * UX trade-off — opening a new tab triggers a fresh auth flow because the
 * tab starts with empty storage. For multi-tab workflows (e.g. an airport
 * workstation with several AOS tabs) this means the second tab redirects
 * back through Keycloak's SSO check. We accept that cost because:
 *
 * 1. Tokens never persist on disk after the user closes all AOS tabs —
 *    smaller window for stolen-token attacks on shared workstations.
 * 2. Keycloak's session cookie carries the SSO state, so the "re-auth"
 *    in a new tab is silent (no credential prompt) as long as the
 *    Keycloak session is still valid.
 * 3. Cross-tab refresh coordination (BroadcastChannel) is on the
 *    roadmap and reduces refresh thrash regardless of where the token
 *    lives.
 *
 * Apps that need long-lived tokens across browser restarts should inject
 * a different `StorageAdapter` (e.g. a localStorage wrapper, or a secure
 * cookie via the host backend) rather than changing this default.
 *
 * Falls back to no-op if `sessionStorage` is unavailable (SSR, locked-down
 * environments) so adapters don't crash on import.
 */
export const createWebSessionStorage = (): StorageAdapter => {
    const available = (() => {
        try {
            return typeof window !== 'undefined' && !!window.sessionStorage
        } catch {
            return false
        }
    })()

    if (!available) {
        return {
            name: 'webSessionStorage(unavailable)',
            get: () => Promise.resolve(null),
            set: () => Promise.resolve(),
            remove: () => Promise.resolve(),
        }
    }

    return {
        name: 'webSessionStorage',
        get: key => Promise.resolve(window.sessionStorage.getItem(key)),
        set: (key, value) => {
            window.sessionStorage.setItem(key, value)
            return Promise.resolve()
        },
        remove: key => {
            window.sessionStorage.removeItem(key)
            return Promise.resolve()
        },
    }
}

export const webSessionStorage = createWebSessionStorage()
